import React, { useEffect, useState, useRef, useCallback } from "react";
import messageStyle from "./mailboxPage.module.css";
import {
  FaRegPaperPlane,
  FaMicrophone,
  FaStop,
  FaTrash,
  FaPaperPlane,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { MdOutlineAnnouncement } from "react-icons/md";
import { PiCodesandboxLogoDuotone, PiMicrophone } from "react-icons/pi";
import speakerImage from "../../../appImges/logo/logo.png";
import { useUser } from "../../../storage/UserContext";
import axios from "axios";
import defaultImage from "../../../appImges/avatartTemplate/default_img.png";
import { io } from "socket.io-client";
import { format, set } from "date-fns";

const apiUrl = process.env.REACT_APP_URL;

const MailboxPage = () => {
  /* The Mailbox Page allows the user to communicate with other website users. The page shows dialogs the user has with other users (new dialogs do not start on this page, but on other pages, including profile pages of users, and a user's Pals page). The user can send text messages and voice messages to other users, and can read text messages and listen to voice messages sent to him / her by other users. */

  const { userData, isLoading, setHasUnreadMessages } = useUser(); // Using the context to get username to fetch dialogs

  const [dialogs, setDialogs] = useState([]);
  const [selectedDialog, setSelectedDialog] = useState(null);
  const [messageInput, setMessageInput] = useState(""); // State for message input
  const socket = useRef(null); // Use ref for socket instance
  const baseURL = `${apiUrl}/`; // Set base URL

  // State for pagination of messages
  const [msgQty, setMsgQty] = useState(4);
  const [msgOffset, setMsgOffset] = useState(4);
  // Voice message variables
  const [isRecording, setIsRecording] = useState(false); // State for recording status
  const [mediaRecorder, setMediaRecorder] = useState(null); // MediaRecorder instance
  const [audioUrl, setAudioUrl] = useState(null); // URL for the recorded audio
  const audioBlobRef = useRef(null); // Ref for audio blob
  const audioChunksRef = useRef([]); // Ref for recorded audio chunks
  const recordingIntervalRef = useRef(null);

  // State for toggling between text and voice mode
  const [isTextMode, setIsTextMode] = useState(true);
  // ref for the end of the messages
  const endOfMessagesRef = useRef(null);
  // variables for pagination of messages to check if there are more messages to load
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [maxChars, setMaxChars] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(0);
  const [currAudioLength, setCurrAudioLength] = useState(0);
  const [feedbackMsg, setFeedbackMsg] = useState(null);

  // Function to toggle between text and voice modes
  const handleToggleMode = () => {
    setIsTextMode((prevMode) => !prevMode);
    setFeedbackMsg(null);
  };
  //Function to check if a dialog has unread messages
  const hasUnreadMessages = useCallback(
    (dialog) => {
      return dialog.messages.some(
        (message) =>
          !message.is_read && message.receiver_username === userData.username
      );
    },
    [userData.username]
  );

  /**
   * Get all dialogs of a user. For each dialog, get an display the last 4 messages (the user can view more messages by clicking on a button).
   */
  const fetchDialogs = useCallback(async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/messages/dialogs/${userData.username}`,
        { withCredentials: true }
      );

      if (response.status === 200 && response.data.success) {
        const dialogs = response.data.dialogs;

        setDialogs(dialogs);
        setSelectedDialog(dialogs[0]);

        // Check for unread messages
        const anyUnread = dialogs.some((dialog) =>
          dialog.messages.some(
            (message) =>
              !message.is_read &&
              message.receiver_username === userData.username
          )
        );
        setHasUnreadMessages(anyUnread);
      }
    } catch (error) {
      console.error("Error fetching dialogs:", error);
    }
  }, [userData.username, setHasUnreadMessages]);

  useEffect(() => {
    if (userData && userData.username && !isLoading) {
      // Fetch dialogs only if userData is available
      fetchDialogs();
    }
  }, [userData, isLoading, fetchDialogs]);

  /**
   * Get the maximum length of a text message and the maximum length of a voice message.*/
  useEffect(() => {
    const fetchData = async () => {
      try {
        const settingsRes = await axios.get(`${apiUrl}/messages/limits`, {
          withCredentials: true,
          params: {},
        });

        if (settingsRes.data && settingsRes.data.success) {
          const fetchedSettings = settingsRes.data.data[0];

          setMaxChars(fetchedSettings.max_text_message_length);
          setMaxSeconds(fetchedSettings.max_voice_message_length);
        }
      } catch (error) {
        console.error("Failed to fetch maximum length settings: ", error);
      }
    };
    fetchData();
  }, []);

  // Scroll event listener to see the last message sent in the dialog
  useEffect(() => {
    if (selectedDialog && selectedDialog.messages && endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedDialog]);

  const fetchMoreMessages = async () => {
    try {
      // Fetch one extra message to check if more are available
      const response = await axios.get(
        `/messages/dialog/${userData.username}/${
          selectedDialog.username2
        }?qty=${msgQty + 1}&offset=${msgOffset}`,
        { withCredentials: true }
      );

      if (response.status === 200 && response.data.messages.length > 0) {
        const fetchedMessages = response.data.messages;

        // Check if we received more messages than the expected quantity
        const hasMore = fetchedMessages.length > msgQty;

        // If more messages were fetched than expected, slice the extra one off
        const messagesToAdd = hasMore
          ? fetchedMessages.slice(0, msgQty)
          : fetchedMessages;

        setSelectedDialog((prevSelectedDialog) => ({
          ...prevSelectedDialog,
          messages: [...prevSelectedDialog.messages, ...messagesToAdd],
        }));

        setMsgOffset((prevOffset) => prevOffset + msgQty); // Increase offset for the next fetch
        setHasMoreMessages(hasMore); // Update hasMoreMessages based on the fetch result
      } else {
        setHasMoreMessages(false); // No more messages to load
      }
    } catch (error) {
      console.error("Error fetching more messages:", error);
      setHasMoreMessages(false); // Disable "See More" on error
    }
  };

  // Function to mark messages as read
  const markMessagesAsRead = useCallback(
    async (dialog) => {
      try {
        const response = await axios.put(
          `${baseURL}messages/updatestatus/${dialog.username2}/${userData.username}`,
          {},
          { withCredentials: true }
        );
        if (response.status === 200) {
          // Update the dialogues
          fetchDialogs();

          // After marking messages as read, check for any remaining unread messages
          const anyUnread = dialogs.some((d) =>
            d.messages.some(
              (message) =>
                !message.is_read &&
                message.receiver_username === userData.username
            )
          );
          setHasUnreadMessages(anyUnread); // Update context
        }
      } catch (error) {
        console.error("Error marking messages as read:", error);
      }
    },
    [baseURL, userData.username, fetchDialogs, dialogs, setHasUnreadMessages]
  );

  // Initialize socket connection
  useEffect(() => {
    if (!socket.current) {
      socket.current = io(`${apiUrl}`, {
        transports: ["websocket"],
      });
      // Join the user to their own room for socket communication
      socket.current.emit("joinRoom", userData.username);

      // Handle receiving a message
      socket.current.on("receiveMessage", (message) => {
        setDialogs((prevDialogs) =>
          prevDialogs.map((dialog) => {
            if (
              dialog.username2 === message.sender_username ||
              dialog.username2 === message.receiver_username
            ) {
              const updatedDialog = {
                ...dialog,
                messages: [message, ...dialog.messages],
                other_user_img_link: dialog.other_user_img_link, // Preserve the other user's image
              };

              if (message.receiver_username === userData.username) {
                updatedDialog.unread = true; // Mark dialog as unread if user is the receiver

                // Use setTimeout to avoid state update during rendering
                setTimeout(() => {
                  setHasUnreadMessages(true);
                }, 0);
              }

              // If the dialog is active, mark the new message as read
              if (
                selectedDialog &&
                selectedDialog.username2 === dialog.username2
              ) {
                setTimeout(() => {
                  markMessagesAsRead(dialog); // Delay this call to prevent conflicts
                }, 0);
              }

              return updatedDialog;
            }
            return dialog;
          })
        );
      });
    }

    return () => {
      if (socket.current) {
        socket.current.disconnect();
        socket.current = null;
      }
    };
  }, [
    userData.username,
    selectedDialog,
    setHasUnreadMessages,
    markMessagesAsRead,
  ]);

  const validateMessage = () => {
    if (isTextMode && messageInput.trim() === "") {
      return false;
    }

    if (isTextMode && messageInput.length > maxChars) {
      setFeedbackMsg("Message exceeds the maximum character limit.");
      return false;
    }

    if (!isTextMode && currAudioLength > maxSeconds) {
      setFeedbackMsg("Recording exceeds the maximum time limit.");
      return false;
    }

    setFeedbackMsg(null);

    return true;
  };

  // Function to handle the send message event
  const handleSendMessage = () => {
    // Prevent sending an empty message
    if (!validateMessage()) {
      return;
    }

    const newMessage = {
      senderUsername: userData.username,
      receiverUsername: selectedDialog.username2,
      textContent: messageInput,
    };
    // Emit the new message to the server
    if (socket.current) {
      socket.current.emit("sendMessage", newMessage);
    }

    setMessageInput(""); // Clear message input
  };

  // Voice message functions
  // Function to handle the stop event and create an audio URL
  const handleStop = () => {
    if (audioChunksRef.current.length === 0) {
      console.error("No audio data recorded");
      return;
    }

    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    audioBlobRef.current = audioBlob;
    const url = URL.createObjectURL(audioBlob);
    setAudioUrl(url);
    audioChunksRef.current = []; // Clear the ref after creating the URL
  };

  // Function to start recording
  const handleStartRecording = async () => {
    try {
      // Reset audio URL for new recording
      setAudioUrl(null);
      audioChunksRef.current = []; // Reset audio chunks ref
      setCurrAudioLength(0);
      setFeedbackMsg(null);

      // Get audio stream from the user's microphone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" }); // Set MIME type
      // Set up event handlers
      recorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      recorder.onstop = handleStop;

      // Store the MediaRecorder instance and start recording
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);

      // Update the length of the recording every second
      recordingIntervalRef.current = setInterval(() => {
        setCurrAudioLength((prevLength) => prevLength + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting voice recording:", error);
    }
  };

  // Function to stop recording
  const handleStopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  // Function to delete the current recording
  const handleDeleteRecording = () => {
    setAudioUrl(null);
    audioChunksRef.current = [];
    audioBlobRef.current = null;
  };
  // Function to generate a random filename
  const generateRandomFilename = (extension) => {
    const randomString = Math.random().toString(36).substring(2, 15);
    return `${randomString}.${extension}`;
  };

  // Function to send voice message
  const handleSendVoiceMessage = async () => {
    if (!validateMessage()) {
      return;
    }

    if (!audioBlobRef.current || !selectedDialog) return;

    const randomFilename = generateRandomFilename("webm"); // Generate a random filename with the 'webm' extension
    const formData = new FormData(); // Create a new FormData object
    formData.append("audio", audioBlobRef.current, randomFilename); // Append the audio blob to the form data
    formData.append("senderUsername", userData.username); // Append the sender's username
    formData.append("receiverUsername", selectedDialog.username2); // Append the receiver's username

    try {
      // Send the audio file to the server
      const response = await axios.post(
        `${baseURL}messages/new/voice`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );

      if (response.status === 201 && response.data.success) {
        const newMessage = {
          sender_username: userData.username,
          receiver_username: selectedDialog.username2,
          audio_url: response.data.audioUrl,
          type: "voice",
          created_at: new Date().toISOString(),
          sender_img: userData.img_link,
          // Use the receiver's image for the first message in the dialog
          receiver_img: selectedDialog.messages[0].receiver_img,
        };

        // Update the state with the new message
        setSelectedDialog((prevSelectedDialog) => ({
          ...prevSelectedDialog,
          messages: [newMessage, ...prevSelectedDialog.messages],
        }));

        setDialogs((prevDialogs) =>
          prevDialogs.map((dialog) =>
            dialog.username2 === selectedDialog.username2
              ? { ...dialog, messages: [newMessage, ...dialog.messages] }
              : dialog
          )
        );
        // Emit the new message to the server
        if (socket.current) {
          socket.current.emit("sendVoiceMessage", {
            senderUsername: userData.username,
            receiverUsername: selectedDialog.username2,
            audioUrl: response.data.audioUrl, // Use the URL returned from the server
          });
        }

        // Clear the audio URL after sending
        setAudioUrl(null);
        // Switch back to text mode
        setIsTextMode(true);
      } else {
        console.error("Failed to send voice message:", response.data.message);
      }
    } catch (error) {
      console.error("Error sending voice message:", error);
    }

    // Clear the audio blob ref
    audioBlobRef.current = null;
  };
  // Function to handle the selection of a dialog from the list and reset the message offset to the initial value
  const handleDialogSelection = (dialog) => {
    setSelectedDialog(dialog); // Set the selected dialog
    setMsgOffset(4); // Reset msgOffset to the initial value (e.g., 4 or 0)
    setHasMoreMessages(true); // Reset hasMoreMessages for the new dialog
    // Mark the messages as read if the dialog contains unread messages
    // Mark messages as read
    if (hasUnreadMessagesInDialog(dialog)) {
      markMessagesAsRead(dialog);
      dialog.unread = false; // Update local state
    }
  };

  // Helper Function to Check for Unread Messages in a Dialog
  const hasUnreadMessagesInDialog = (dialog) => {
    return dialog.messages.some(
      (message) =>
        !message.is_read && message.receiver_username === userData.username
    );
  };

  useEffect(() => {
    // Ensure that the first dialog's messages are marked as read when the mailbox component mounts
    if (selectedDialog && hasUnreadMessages(selectedDialog)) {
      markMessagesAsRead(selectedDialog); // Automatically mark messages as read for the first active dialog
    }
  }, [selectedDialog, hasUnreadMessages, markMessagesAsRead]);

  return (
    <div className={messageStyle.mainMessage}>
      {/* Messages section  */}
      <div className={messageStyle.people}>
        <h2>Messages</h2>
        {dialogs.length === 0 ? (
          <p className={messageStyle.noMessagesTxt}>No dialogs available.</p> // If no dialogs available
        ) : (
          dialogs.map((dialog, index) => {
            const isUnread = hasUnreadMessages(dialog);
            const isActive =
              selectedDialog && selectedDialog.username2 === dialog.username2;

            // Apply different colors based on unread status and whether the dialog is active
            let backgroundColor;
            if (isActive) {
              backgroundColor = "#b7d0e1"; // Active dialog color
            } else if (isUnread || dialog.unread) {
              // Check unread flag or the function
              backgroundColor = "#5f84a2"; // Color for unread dialog
            } else {
              backgroundColor = "transparent"; // Default color for read dialogs
            }

            return (
              <div
                key={index}
                style={{ backgroundColor }}
                className={messageStyle.person}
                onClick={() => handleDialogSelection(dialog)}
              >
                <figure className={messageStyle.dialogImg}>
                  {dialog.username2 === "Speaker" ? (
                    <img src={speakerImage} alt="Speaker" />
                  ) : (
                    <img
                      src={
                        dialog.other_user_img_link
                          ? `${baseURL}${dialog.other_user_img_link}`
                          : defaultImage
                      }
                      alt={dialog.username2}
                    />
                  )}
                </figure>
                <div>
                  <h3>{dialog.username2}</h3>
                  <p className={messageStyle.messagePreview}>
                    {dialog.messages[0]?.text_content || (
                      <>
                        Voice message <PiMicrophone />
                      </>
                    )}
                  </p>
                </div>
                <div className={messageStyle.date}>
                  <p>
                    {dialog.messages[0]?.created_at
                      ? format(
                          new Date(dialog.messages[0].created_at),
                          "dd/MM/yyyy"
                        )
                      : "No date"}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Messages section - where user can see all the messages of the dialog and write a message*/}

      <div className={messageStyle.message}>
        {selectedDialog ? (
          <div className={messageStyle.messages}>
            {/* "See More" Button */}
            {hasMoreMessages && selectedDialog.messages.length > 3 && (
              <p
                className={messageStyle.seeMoreBtn}
                onClick={fetchMoreMessages}
              >
                See More
              </p>
            )}
            {selectedDialog.messages
              .slice()
              .reverse()
              .map((message, index, array) => {
                const messageDate = new Date(message.created_at);
                const previousMessageDate =
                  index > 0 ? new Date(array[index - 1].created_at) : null;
                const isSameDay =
                  previousMessageDate &&
                  messageDate.toDateString() ===
                    previousMessageDate.toDateString();

                const isToday =
                  messageDate.toDateString() === new Date().toDateString();

                return (
                  <React.Fragment key={index}>
                    {/* Show date or "Today" label if not the same day as the previous message */}
                    {!isSameDay && (
                      <div className={messageStyle.dateLabel}>
                        {isToday ? (
                          <span>Today</span>
                        ) : (
                          <span>{format(messageDate, "dd/MM/yyyy")}</span>
                        )}
                      </div>
                    )}
                    <div
                      className={
                        message.sender_username === userData.username
                          ? messageStyle.sentByUser
                          : messageStyle.respBorder
                      }
                    >
                      {message.sender_username !== userData.username ? (
                        <div className={messageStyle.respBorder}>
                          <figure className={messageStyle.imagestyle}>
                            {message.sender_username === "Speaker" && (
                              <img
                                className={messageStyle.speakerImage}
                                src={speakerImage}
                                alt="Speaker"
                              />
                            )}
                            {message.sender_username !== "Speaker" && (
                              <img
                                src={
                                  selectedDialog.other_user_img_link &&
                                  selectedDialog.other_user_img_link !== "null"
                                    ? `${baseURL}${selectedDialog.other_user_img_link}`
                                    : defaultImage
                                }
                                alt="face"
                              />
                            )}
                          </figure>
                          <div className={messageStyle.response}>
                            <p>{message.text_content}</p>
                            {message.audio_url && (
                              <audio
                                controls
                                src={`${baseURL}${message.audio_url}`}
                              ></audio>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={messageStyle.sentByUser}>
                          <p>{message.text_content}</p>
                          {message.audio_url && (
                            <audio
                              controls
                              src={`${baseURL}${message.audio_url}`}
                            ></audio>
                          )}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
          </div>
        ) : null}

        {/* Message Input Area  */}
        {dialogs.length !== 0 && selectedDialog.username2 !== "Speaker" ? (
          <div className={messageStyle.addmessage}>
            <figure className={messageStyle.imagestyle}>
              <img
                src={
                  userData.img_link && userData.img_link !== "default_img_link"
                    ? userData.img_link
                    : defaultImage
                }
                alt="avatar"
                onError={(e) => {
                  e.target.src = defaultImage;
                  e.target.onerror = null; // Prevents infinite loop if the default image is also not found
                }}
              />
            </figure>

            <div className={messageStyle.textDiv}>
              <textarea
                className={messageStyle.textarea}
                placeholder={`Type your message here...`}
                value={messageInput}
                onChange={(e) => {
                  setMessageInput(e.target.value);
                  validateMessage();
                }}
              />
            </div>
            {isTextMode ? (
              <button
                className={messageStyle.button3}
                onClick={handleSendMessage}
                onDoubleClick={handleToggleMode}
              >
                <FaRegPaperPlane />
              </button>
            ) : (
              <button
                id="microphoneButton"
                className={messageStyle.button3}
                onClick={
                  isRecording ? handleStopRecording : handleStartRecording
                }
              >
                {isRecording ? <FaStop /> : <FaMicrophone />}
              </button>
            )}
          </div>
        ) : null}
        {selectedDialog && selectedDialog.username2 === "Speaker" && (
          <p className={messageStyle.announcements}>
            <MdOutlineAnnouncement />
            <span>
              This chat contains announcements by Speaker staff. If you have any
              questions, feel free to <Link to="/acontact">contact us</Link>.
            </span>
          </p>
        )}

        {feedbackMsg && <span>{feedbackMsg}</span>}

        {/* Audio player for recorded message */}
        {audioUrl && (
          <div className={messageStyle.audioPlayer}>
            <audio controls src={audioUrl}></audio>
            <button
              className={messageStyle.button3}
              onClick={handleSendVoiceMessage}
            >
              <FaPaperPlane />
            </button>
            <button
              className={messageStyle.button3}
              onClick={() => {
                handleDeleteRecording();
                handleToggleMode();
              }}
            >
              <FaTrash />
            </button>
          </div>
        )}
        {/* end  */}
      </div>
    </div>
  );
};

export default MailboxPage;
